/* eslint-disable id-denylist -- Hook fetch returns use `data` per useConversation/useCharacter convention; API response uses backend `data` field. */
import { useCallback, useRef, useState } from "react";
import createModuleLogger from "../../../infra/logger/createModuleLogger";
import { useAppDispatch } from "../../hook";
import { useTranslation } from "../../localization";
import fetchBlockedCreatorsPage from "./blockedCreatorsSlice";
import { BlockedCreatorsFlow } from "./types";
const blockedCreatorsLogger = createModuleLogger("useBlockedCreators");
const resolveBlockedCreatorsApiError = (error) => {
    if (typeof error === "string") {
        return {
            message: error,
            errorCode: undefined,
        };
    }
    return error;
};
export default function useBlockedCreators() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [stateBlockedCreators, setStateBlockedCreators] = useState([]);
    const [stateLastKey, setStateLastKey] = useState(null);
    const [stateIsLoading, setStateIsLoading] = useState(false);
    const [stateIsInitialLoadCompleted, setStateIsInitialLoadCompleted] = useState(false);
    const [stateBlockedCreatorsError, setStateBlockedCreatorsError] = useState(null);
    const refIsLoading = useRef(false);
    const resetError = useCallback(() => {
        setStateBlockedCreatorsError(null);
    }, []);
    const loadBlockedCreators = useCallback(async ({ lastKey } = {}) => {
        if (refIsLoading.current) {
            return { data: null, error: null };
        }
        refIsLoading.current = true;
        setStateIsLoading(true);
        const isLoadMoreRequest = lastKey !== undefined;
        const errorFlow = isLoadMoreRequest ? BlockedCreatorsFlow.LOAD_MORE_BLOCKED_CREATORS : BlockedCreatorsFlow.LOAD_BLOCKED_CREATORS;
        try {
            const paginatedResponse = await dispatch(fetchBlockedCreatorsPage(lastKey ? { lastKey } : undefined)).unwrap();
            const { data: incomingCreators } = paginatedResponse;
            setStateBlockedCreators((previousCreators) => (isLoadMoreRequest ? [...previousCreators, ...incomingCreators] : incomingCreators));
            setStateLastKey(paginatedResponse.lastKey ?? null);
            setStateBlockedCreatorsError(null);
            return { data: paginatedResponse, error: null };
        }
        catch (error) {
            const apiError = resolveBlockedCreatorsApiError(error);
            const errorMessage = apiError.message || t("error:fallbackError");
            setStateBlockedCreatorsError({
                errorType: errorFlow,
                errorMessage,
            });
            blockedCreatorsLogger.error(`Error fetching blocked creators: ${errorMessage}`, apiError);
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
        finally {
            refIsLoading.current = false;
            setStateIsLoading(false);
            setStateIsInitialLoadCompleted(true);
        }
    }, [dispatch, t]);
    const loadMoreBlockedCreators = useCallback(async () => {
        if (!stateLastKey || refIsLoading.current) {
            return { data: null, error: null };
        }
        return loadBlockedCreators({ lastKey: stateLastKey });
    }, [loadBlockedCreators, stateLastKey]);
    const removeBlockedCreator = (creatorId) => {
        setStateBlockedCreators((previousCreators) => {
            return previousCreators.filter((creator) => creator.id !== creatorId);
        });
    };
    const retryInitialLoad = useCallback(async () => {
        resetError();
        setStateIsInitialLoadCompleted(false);
        return loadBlockedCreators();
    }, [loadBlockedCreators, resetError]);
    return {
        blockedCreators: stateBlockedCreators,
        hasMoreBlockedCreators: stateLastKey !== null,
        isLoading: stateIsLoading,
        hasInitialLoadCompleted: stateIsInitialLoadCompleted,
        errorType: stateBlockedCreatorsError?.errorType || null,
        errorMessage: stateBlockedCreatorsError?.errorMessage || null,
        loadBlockedCreators,
        loadMoreBlockedCreators,
        removeBlockedCreator,
        retryInitialLoad,
        resetError,
    };
}
